import React, { useContext, useEffect } from "react";
import { Button, Card, CardBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../css/volunteerBlockTab.module.scss";
import { useBlockStatusService } from "../api/useBlockVolunteerQuery";
import { formatDate } from "common/service/CommonService";
import { BreadcrumbContext } from "Layouts/Header";
import { VOLUNTEER_PATHS } from "Routes/paths";

interface VolunteerBlockTabProps {
  volunteerId: number;
}

// ─── Skeleton loader while API fetches ────────────────────────────────────────
const BlockStatusSkeleton: React.FC = () => (
  <div className={styles.skeletonWrap}>
    <div className={styles.skeletonCircle} />
    <div className={`${styles.skeletonLine} ${styles.wide}`} />
    <div className={`${styles.skeletonLine} ${styles.medium}`} />
    <div className={`${styles.skeletonLine} ${styles.short}`} />
  </div>
);

interface BlockDetailsProps {
  blocktype?: string | null;
  startdate?: string | null;
  enddate?: string | null;
  blockreason?: string | null;
}

// ─── BLOCK_VERIFICATION_PENDING state ────────────────────────────────────────
const PendingVerificationState: React.FC<BlockDetailsProps> = ({
  blocktype,
  startdate,
  enddate,
  blockreason,
}) => (
  <div className={`${styles.statusContainer} ${styles.pendingState}`}>
    <div className={styles.statusMsgCol}>
      <div className={`${styles.statusIconWrap} ${styles.pending}`}>
        <div className={styles.statusPulse} />
        <div className={styles.statusIcon}>
          <i className="ri-time-line" />
        </div>
      </div>
      <p className={styles.statusTitle}>Block Verification Pending</p>
      <p className={styles.statusSubtext}>
        This block request is awaiting verification. 
        Access to volunteer participation activities will be updated once the request is approved or rejected.
      </p>
      <span className={`${styles.statusBadge} ${styles.badgePending}`}>
        <i className="ri-hourglass-line" />
        Awaiting Verification
      </span>
    </div>

    {(blockreason || blocktype || startdate) && (
      <div className={styles.statusDetailsCol}>
        <div className={styles.blockInfoGrid}>
          {blocktype && (
            <div className={`${styles.infoCell} ${blocktype?.toLowerCase() === "permanent" ? styles.span3 : styles.span2}`}>
              <span className={styles.cellLabel}>Type</span>
              <span className={styles.cellValue}>{blocktype}</span>
            </div>
          )}
          {startdate && (
            <div className={`${styles.infoCell} ${blocktype?.toLowerCase() === "permanent" ? styles.span3 : styles.span2}`}>
              <span className={styles.cellLabel}>Start Date</span>
              <span className={styles.cellValue}>{formatDate(startdate)}</span>
            </div>
          )}
          {blocktype?.toLowerCase() !== "permanent" && enddate && (
            <div className={`${styles.infoCell} ${styles.span2}`}>
              <span className={styles.cellLabel}>End Date</span>
              <span className={styles.cellValue}>{formatDate(enddate)}</span>
            </div>
          )}
          {blockreason && (
            <div className={`${styles.infoCell} ${styles.span6}`}>
              <span className={styles.cellLabel}>Reason</span>
              <span className={styles.cellValue}>{blockreason}</span>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

// ─── PRE_ASSIGNMENT_BLOCK state ───────────────────────────────────────────────
const PreAssignmentBlockedState: React.FC<BlockDetailsProps> = ({
  blocktype,
  startdate,
  enddate,
  blockreason,
}) => (
  <div className={`${styles.statusContainer} ${styles.preBlockState}`}>
    <div className={styles.statusMsgCol}>
      <div className={`${styles.statusIconWrap} ${styles.preblock}`}>
        <div className={styles.statusPulse} />
        <div className={styles.statusIcon}>
          <i className="ri-user-forbid-line" />
        </div>
      </div>
      <p className={styles.statusTitle}>Volunteer is Pre-Assignment Blocked</p>
      <p className={styles.statusSubtext}>
        This volunteer is currently restricted from participating in any study activities. 
        Study assignment and enrollment will remain unavailable until the block is removed.
      </p>
      <span className={`${styles.statusBadge} ${styles.badgeBlocked}`}>
        <i className="ri-lock-line" />
        Pre-Assignment Block
      </span>
    </div>

    {(blockreason || blocktype || startdate) && (
      <div className={styles.statusDetailsCol}>
        <div className={styles.blockInfoGrid}>
          {blocktype && (
            <div className={`${styles.infoCell} ${blocktype?.toLowerCase() === "permanent" ? styles.span3 : styles.span2}`}>
              <span className={styles.cellLabel}>Type</span>
              <span className={styles.cellValue}>{blocktype}</span>
            </div>
          )}
          {startdate && (
            <div className={`${styles.infoCell} ${blocktype?.toLowerCase() === "permanent" ? styles.span3 : styles.span2}`}>
              <span className={styles.cellLabel}>Start Date</span>
              <span className={styles.cellValue}>{formatDate(startdate)}</span>
            </div>
          )}
          {blocktype?.toLowerCase() !== "permanent" && enddate && (
            <div className={`${styles.infoCell} ${styles.span2}`}>
              <span className={styles.cellLabel}>End Date</span>
              <span className={styles.cellValue}>{formatDate(enddate)}</span>
            </div>
          )}
          {blockreason && (
            <div className={`${styles.infoCell} ${styles.span6}`}>
              <span className={styles.cellLabel}>Reason</span>
              <span className={styles.cellValue}>{blockreason}</span>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

interface PostEnrollmentBlockedStateProps extends BlockDetailsProps {
  studycode?: string | null;
}

// ─── POST_ENROLLMENT_BLOCK state ──────────────────────────────────────────────
const PostEnrollmentBlockedState: React.FC<PostEnrollmentBlockedStateProps> = ({
  studycode,
  blocktype,
  startdate,
  enddate,
  blockreason,
}) => (
  <div className={`${styles.statusContainer} ${styles.postBlockState}`}>
    <div className={styles.statusMsgCol}>
      <div className={`${styles.statusIconWrap} ${styles.postblock}`}>
        <div className={styles.statusPulse} />
        <div className={styles.statusIcon}>
          <i className="ri-prohibited-line" />
        </div>
      </div>
      <p className={styles.statusTitle}>Volunteer is Post-Enrollment Blocked</p>
      <p className={styles.statusSubtext}>
        This volunteer has been restricted from continuing study participation after enrollment. 
        Further study activities are not permitted until the block is lifted.
      </p>
      <div className={styles.statusBadgesRow}>
        {studycode && (
          <div className={styles.studyCodeTag}>
            <i className="ri-flask-line" />
            Study: {studycode}
          </div>
        )}
        <span className={`${styles.statusBadge} ${styles.badgePostBlock}`}>
          <i className="ri-lock-2-line" />
          Post-Enrollment Block
        </span>
      </div>
    </div>

    {(blockreason || blocktype || startdate) && (
      <div className={styles.statusDetailsCol}>
        <div className={styles.blockInfoGrid}>
          {blocktype && (
            <div className={`${styles.infoCell} ${blocktype?.toLowerCase() === "permanent" ? styles.span3 : styles.span2}`}>
              <span className={styles.cellLabel}>Type</span>
              <span className={styles.cellValue}>{blocktype}</span>
            </div>
          )}
          {startdate && (
            <div className={`${styles.infoCell} ${blocktype?.toLowerCase() === "permanent" ? styles.span3 : styles.span2}`}>
              <span className={styles.cellLabel}>Start Date</span>
              <span className={styles.cellValue}>{formatDate(startdate)}</span>
            </div>
          )}
          {blocktype?.toLowerCase() !== "permanent" && enddate && (
            <div className={`${styles.infoCell} ${styles.span2}`}>
              <span className={styles.cellLabel}>End Date</span>
              <span className={styles.cellValue}>{formatDate(enddate)}</span>
            </div>
          )}
          {blockreason && (
            <div className={`${styles.infoCell} ${styles.span6}`}>
              <span className={styles.cellLabel}>Reason</span>
              <span className={styles.cellValue}>{blockreason}</span>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

// ─── ACTIVE (default) state ───────────────────────────────────────────────────
const ActiveState: React.FC<{ onBlockClick: () => void }> = ({ onBlockClick }) => (
  <div className={styles.innerDashedContainer}>
    {/* Animated concentric avatar circles */}
    <div className={styles.avatarContainer}>
      <div className={styles.outerRing}></div>
      <div className={styles.middleRing}></div>
      <div className={styles.innerRing}></div>
      <div className={styles.avatarCircle}>
        <i className="ri-user-unfollow-line"></i>
      </div>
    </div>

    {/* Header Title */}
    <h4 className="fw-bold text-dark mb-1">
      Restrict <span className="text-danger">Volunteer Participation</span>
    </h4>

    {/* Small red underline */}
    <div className={styles.dividerLine}></div>

    {/* Description */}
    <p className="text-muted fs-14" style={{ maxWidth: "540px", lineHeight: "1.6" }}>
      Blocking a volunteer restricts them from being scheduled, assigned, or enrolled in any
      clinical study based on violations or administrative reasons.
    </p>

    {/* Features Bar */}
    <div className={styles.featuresBar}>
      <div className={styles.featureItem}>
        <div className={styles.iconCircle}>
          <i className="ri-calendar-close-line" />
        </div>
        <div className={styles.textWrap}>
          <span className={styles.featureTitle}>Pre-Block</span>
          <span className={styles.featureSub}>Prevents scheduling and study assigning</span>
        </div>
      </div>
      <div className={styles.featureItem}>
        <div className={styles.iconCircle}>
          <i className="ri-shield-flash-line" />
        </div>
        <div className={styles.textWrap}>
          <span className={styles.featureTitle}>Policy Enforcement</span>
          <span className={styles.featureSub}>Enforces protocol and regulatory compliance</span>
        </div>
      </div>
      <div className={styles.featureItem}>
        <div className={styles.iconCircle}>
          <i className="ri-file-list-3-line" />
        </div>
        <div className={styles.textWrap}>
          <span className={styles.featureTitle}>Audit Trail</span>
          <span className={styles.featureSub}>All block actions are logged and traceable</span>
        </div>
      </div>
    </div>

    {/* Action Button */}
    <Button
      color="danger"
      className="px-4 py-2 mb-2 mt-1 fw-semibold d-flex align-items-center gap-2 border-0 shadow-sm"
      style={{ borderRadius: "6px", background: "#d32f2f" }}
      onClick={onBlockClick}
    >
      <i className="ri-lock-line fs-16" />
      Block Volunteer
    </Button>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const VolunteerBlockTab: React.FC<VolunteerBlockTabProps> = ({ volunteerId }) => {
  const navigate = useNavigate();
  const { BlockStatusQuery } = useBlockStatusService(volunteerId || null);
  const breadcrumbContext = useContext(BreadcrumbContext);

  const statusData = BlockStatusQuery?.data?.data?.[0];
  const volunteerStatus: string = statusData?.volunteerstatus ?? "ACTIVE";
  const studycode: string | null = statusData?.studycode ?? null;
  const blocktype: string | null = statusData?.blocktype ?? null;
  const startdate: string | null = statusData?.startdate ?? null;
  const enddate: string | null = statusData?.enddate ?? null;
  const blockreason: string | null = statusData?.blockreason ?? null;
  const isLoading = BlockStatusQuery?.isLoading || BlockStatusQuery?.isFetching;

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Volunteer", link: VOLUNTEER_PATHS.LIST },
      { text: "Block Record" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const handleBlockClick = () => {
    localStorage.setItem("activevolunteerid", String(volunteerId));
    navigate("/pre-block-volunteer/add");
  };

  const renderContent = () => {
    if (!volunteerId || isLoading) return <BlockStatusSkeleton />;

    switch (volunteerStatus) {
      case "BLOCK_VERIFICATION_PENDING":
        return (
          <PendingVerificationState
            blocktype={blocktype}
            startdate={startdate}
            enddate={enddate}
            blockreason={blockreason}
          />
        );
      case "PRE_ASSIGNMENT_BLOCK":
        return (
          <PreAssignmentBlockedState
            blocktype={blocktype}
            startdate={startdate}
            enddate={enddate}
            blockreason={blockreason}
          />
        );
      case "POST_ENROLLMENT_BLOCK":
        return (
          <PostEnrollmentBlockedState
            studycode={studycode}
            blocktype={blocktype}
            startdate={startdate}
            enddate={enddate}
            blockreason={blockreason}
          />
        );
      case "ACTIVE":
      default:
        return <ActiveState onBlockClick={handleBlockClick} />;
    }
  };

  return (
    <Card
      className="shadow-sm border-0"
      style={{ borderRadius: "16px", margin: "0px 10px 16px 10px", overflow: "hidden" }}
    >
      <CardBody className={`p-3 bg-white ${styles.cardBody}`}>
        {renderContent()}
      </CardBody>
    </Card>
  );
};

export default VolunteerBlockTab;
